import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';
import { Star, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    page_title: 'Leave a verified review',
    intro: 'Thanks for ordering with Penlab Peptide. Your honest review helps other research professionals choose with confidence.',
    verified: 'Verified purchase',
    items_heading: 'Products in your order',
    rating_label: 'Your rating',
    title_label: 'Headline',
    title_placeholder: 'e.g. Solid potency, fast shipping',
    text_label: 'Your review',
    text_placeholder: 'Describe your experience, purity, packaging, delivery, observations. No medical claims please.',
    name_label: 'Display name (optional)',
    name_placeholder: 'How would you like to be credited?',
    submit: 'Submit review',
    submitted_pending: 'Thanks! Your review is pending approval and will appear publicly once moderated.',
    already_reviewed: 'Review submitted',
    not_yet_reviewed: 'Not yet reviewed',
    invalid_link: 'This review link is invalid or has expired.',
    rating_helper: 'Click the stars to rate (1-5)',
    legal: 'By submitting, you confirm your review reflects your own research observations and contains no medical or therapeutic claims.',
  },
  es: {
    page_title: 'Deja una reseña verificada',
    intro: 'Gracias por comprar en Penlab Peptide. Tu reseña honesta ayuda a otros profesionales de la investigación a elegir con confianza.',
    verified: 'Compra verificada',
    items_heading: 'Productos de tu pedido',
    rating_label: 'Tu valoración',
    title_label: 'Título',
    title_placeholder: 'ej. Buena potencia y envío rápido',
    text_label: 'Tu reseña',
    text_placeholder: 'Describe tu experiencia, pureza, empaque, entrega, observaciones. Sin afirmaciones médicas, por favor.',
    name_label: 'Nombre público (opcional)',
    name_placeholder: '¿Cómo quieres ser acreditado?',
    submit: 'Enviar reseña',
    submitted_pending: '¡Gracias! Tu reseña está pendiente de aprobación y se publicará una vez moderada.',
    already_reviewed: 'Reseña enviada',
    not_yet_reviewed: 'Aún sin reseña',
    invalid_link: 'Este enlace de reseña no es válido o ha expirado.',
    rating_helper: 'Haz clic en las estrellas para valorar (1-5)',
    legal: 'Al enviar, confirmas que tu reseña refleja tus propias observaciones de investigación y no contiene afirmaciones médicas ni terapéuticas.',
  },
  ru: {
    page_title: 'Оставьте проверенный отзыв',
    intro: 'Спасибо за заказ в Penlab Peptide. Ваш честный отзыв помогает другим исследователям делать уверенный выбор.',
    verified: 'Проверенная покупка',
    items_heading: 'Товары в вашем заказе',
    rating_label: 'Ваша оценка',
    title_label: 'Заголовок',
    title_placeholder: 'напр., Высокая активность, быстрая доставка',
    text_label: 'Ваш отзыв',
    text_placeholder: 'Опишите ваш опыт, чистоту, упаковку, доставку, наблюдения. Без медицинских утверждений.',
    name_label: 'Имя для отображения (необязательно)',
    name_placeholder: 'Как вас подписать?',
    submit: 'Отправить отзыв',
    submitted_pending: 'Спасибо! Ваш отзыв ожидает модерации и будет опубликован после одобрения.',
    already_reviewed: 'Отзыв отправлен',
    not_yet_reviewed: 'Пока без отзыва',
    invalid_link: 'Ссылка на отзыв недействительна или истекла.',
    rating_helper: 'Нажмите звезды, чтобы оценить (1-5)',
    legal: 'Отправляя отзыв, вы подтверждаете, что он отражает ваши собственные исследовательские наблюдения и не содержит медицинских утверждений.',
  },
};

function StarPicker({ value, onChange, size = 'lg', readonly = false, testId }) {
  const px = size === 'lg' ? 'w-8 h-8' : 'w-4 h-4';
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          disabled={readonly}
          onClick={() => !readonly && onChange?.(n)}
          aria-label={`${n} stars`}
          className={`${readonly ? '' : 'hover:scale-110'} transition-transform`}
          data-testid={`${testId}-star-${n}`}
        >
          <Star
            className={`${px} ${n <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        </button>
      ))}
    </div>
  );
}

export default function ReviewSubmitPage() {
  const { token } = useParams();
  const { currentLanguage } = useLanguage();
  const t = COPY[currentLanguage] || COPY.en;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [forms, setForms] = useState({}); // { product_id: { rating, title, text, name } }
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get(`${API}/reviews/token/${token}`);
        setData(res.data);
      } catch (e) {
        setError(t.invalid_link);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token, t.invalid_link]);

  const updateForm = (pid, patch) => {
    setForms((prev) => ({ ...prev, [pid]: { ...(prev[pid] || {}), ...patch } }));
  };

  const submit = async (pid) => {
    const f = forms[pid] || {};
    if (!f.rating || !f.title || !f.text) {
      toast.error('Rating, headline and review text are required');
      return;
    }
    try {
      setSubmitting(pid);
      await axios.post(`${API}/reviews/token/${token}`, {
        product_id: pid,
        rating: f.rating,
        title: f.title,
        text: f.text,
        reviewer_name: f.name || null,
        language: currentLanguage,
      });
      toast.success(t.submitted_pending);
      // Refresh state
      const fresh = await axios.get(`${API}/reviews/token/${token}`);
      setData(fresh.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit review');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <>
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-10 min-h-[60vh]" data-testid="review-page">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">{t.page_title}</h1>
          <p className="text-gray-600 mt-2 max-w-2xl">{t.intro}</p>
          <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium">
            <ShieldCheck className="w-3.5 h-3.5" />
            {t.verified}
          </div>
        </header>

        {loading && (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 p-4" data-testid="review-error">
            {error}
          </div>
        )}

        {!loading && data && (
          <section className="space-y-6">
            <h2 className="text-base md:text-lg font-semibold text-gray-800">
              {t.items_heading}
            </h2>
            {data.items.map((item) => {
              const f = forms[item.product_id] || {};
              const reviewed = item.already_reviewed;
              return (
                <article
                  key={item.product_id}
                  className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
                  data-testid={`review-item-${item.product_id}`}
                >
                  <div className="flex items-start gap-4">
                    {item.image_url && (
                      <img src={item.image_url} alt={item.product_name?.[currentLanguage] || item.product_name?.en || 'Product'} className="w-16 h-16 rounded-md object-cover bg-gray-50" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 truncate">
                        {item.product_name?.[currentLanguage] || item.product_name?.en}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {item.selected_variant && <>· {item.selected_variant}</>}
                        {item.quantity && <> · ×{item.quantity}</>}
                      </div>
                      {reviewed ? (
                        <div
                          className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                          data-testid={`review-success-${item.product_id}`}
                        >
                          {t.already_reviewed} ({item.review_status})
                        </div>
                      ) : (
                        <div className="mt-2 text-xs text-gray-500">{t.not_yet_reviewed}</div>
                      )}
                    </div>
                  </div>

                  {!reviewed && (
                    <div className="mt-5 grid gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          {t.rating_label}
                        </label>
                        <StarPicker
                          value={f.rating || 0}
                          onChange={(n) => updateForm(item.product_id, { rating: n })}
                          testId={`star-picker-${item.product_id}`}
                        />
                        <p className="text-xs text-gray-500 mt-1">{t.rating_helper}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          {t.title_label}
                        </label>
                        <input
                          type="text"
                          maxLength={140}
                          value={f.title || ''}
                          onChange={(e) => updateForm(item.product_id, { title: e.target.value })}
                          placeholder={t.title_placeholder}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          data-testid={`review-title-${item.product_id}`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          {t.text_label}
                        </label>
                        <textarea
                          rows={4}
                          maxLength={2000}
                          value={f.text || ''}
                          onChange={(e) => updateForm(item.product_id, { text: e.target.value })}
                          placeholder={t.text_placeholder}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          data-testid={`review-text-${item.product_id}`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          {t.name_label}
                        </label>
                        <input
                          type="text"
                          maxLength={120}
                          value={f.name || ''}
                          onChange={(e) => updateForm(item.product_id, { name: e.target.value })}
                          placeholder={data.customer_name_hint || t.name_placeholder}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          data-testid={`review-name-${item.product_id}`}
                        />
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">{t.legal}</p>
                      <Button
                        onClick={() => submit(item.product_id)}
                        disabled={submitting === item.product_id}
                        className="bg-teal-600 hover:bg-teal-700 text-white w-full sm:w-auto"
                        data-testid={`review-submit-${item.product_id}`}
                      >
                        {submitting === item.product_id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        {t.submit}
                      </Button>
                    </div>
                  )}
                </article>
              );
            })}
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}
